import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RoleDropdown.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RoleOption {
  value: string;
  label: string;
  description: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** API values — never translated, safe to send in request bodies */
export const ROLE_VALUES = ['ADMIN', 'MEMBER', 'GUEST'] as const;
export type RoleValue = typeof ROLE_VALUES[number];

// ─── Component ────────────────────────────────────────────────────────────────

export interface RoleDropdownProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}

export function RoleDropdown({ value, onChange, id }: RoleDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const roleOptions: RoleOption[] = ROLE_VALUES.map(v => ({
    value: v,
    label: t(`employees.roles_${v}_label`),
    description: t(`employees.roles_${v}_description`),
  }));

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = roleOptions.find(r => r.value === value);

  return (
    <div className={styles.roleDropdown} ref={ref}>
      <button
        id={id}
        type="button"
        className={[
          styles.roleButton,
          open ? styles.roleButtonOpen : '',
          !selected ? styles.roleButtonPlaceholder : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setOpen(prev => !prev)}
      >
        <span>{selected ? selected.label : t('user.edit.selectRole', { defaultValue: 'Select Role' })}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className={styles.rolePanel}>
          {roleOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={[
                styles.roleOption,
                value === option.value ? styles.roleOptionSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span className={styles.roleOptionName}>{option.label}</span>
              <span className={styles.roleOptionDesc}>{option.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
